import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title?: string;
  description?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article' | 'event';
}

const defaultMeta = {
  title: "Winner's Cup 2027 | Premier Gymnastics Competition",
  description: "February 26–28, 2027 · Ontario Convention Center, California. The premier West Coast gymnastics competition, hosted by Winner's Gymnastics.",
  image: "/og-image.png",
  url: "https://winnerscup.com",
  type: "website" as const,
};

const SEO = ({
  title,
  description = defaultMeta.description,
  image = defaultMeta.image,
  url = defaultMeta.url,
  type = defaultMeta.type,
}: SEOProps) => {
  const fullTitle = title
    ? `${title} | Winner's Cup 2027`
    : defaultMeta.title;

  const fullImageUrl = image.startsWith('http') 
    ? image 
    : `${defaultMeta.url}${image}`;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />

      {/* Canonical (self-referencing per route) */}
      <link rel="canonical" href={url} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={fullImageUrl} />
      <meta property="og:site_name" content="Winner's Cup" />

      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={url} />
      <meta property="twitter:title" content={fullTitle} />
      <meta property="twitter:description" content={description} />
      <meta property="twitter:image" content={fullImageUrl} />

      {/* Additional SEO */}
      <meta name="robots" content="index, follow" />
      <meta name="language" content="English" />
      <meta name="author" content="Winner's Gymnastics" />
      
      {/* Event-specific structured data */}
      {type === 'event' && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SportsEvent",
            "name": "Winner's Cup 2027",
            "startDate": "2027-02-26",
            "endDate": "2027-02-28",
            "eventStatus": "https://schema.org/EventScheduled",
            "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode",
            "sport": "Gymnastics",
            "location": {
              "@type": "Place",
              "name": "Ontario Convention Center",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "2000 E Convention Center Way",
                "addressLocality": "Ontario",
                "addressRegion": "CA",
                "postalCode": "91764",
                "addressCountry": "US"
              }
            },
            "organizer": {
              "@type": "Organization",
              "name": "Winner's Gymnastics",
              "url": "https://winnersgymnastics.com"
            },
            "description": description,
            "image": fullImageUrl
          })}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;